/**
 * Task 23's view half — the append-only fleet-audit trail for one device,
 * reached from `CommandHistory`'s "View all" link once a device has more
 * than the embedded list's 5-row cap. `GET /fleet/audit` is being built in
 * parallel (Global Constraints) — a failure here is a clear error state,
 * never a fabricated empty history.
 *
 * Table columns mirror the mockup's Command history table
 * (`fleet-device-support.html:251-264`: When / Action / By / Reason /
 * Result), adapted to the audit event's own shape (`kind`/`actor`/`detail`
 * rather than a command's `state`). Filter pills reuse the same
 * `min-h-[44px]` / `rounded-xl` vocabulary `FleetTable.tsx` uses for its own
 * release-status filter, and result pills reuse the platform's fixed
 * emerald/amber/rose/gray classes verbatim — there is no `<Pill>` component
 * in this codebase.
 */
import { useCallback, useEffect, useState } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import type { FleetAuditEvent } from '@platform/shared-types';
import { fleetSupportApi } from '../../../services/fleetSupportApi';
import { RubyLoader } from '../../../components/RubyLoader';

const KIND_META: Record<FleetAuditEvent['kind'], { label: string; cls: string }> = {
  command_created: { label: 'Command created', cls: 'bg-amber-500/15 text-amber-600' },
  command_state: { label: 'Command state', cls: 'bg-gray-400/20 text-gray-500' },
  command_refused: { label: 'Command refused', cls: 'bg-rose-500/15 text-rose-600' },
  code_issued: { label: 'Code issued', cls: 'bg-amber-500/15 text-amber-600' },
  code_redeemed: { label: 'Code redeemed', cls: 'bg-emerald-500/15 text-emerald-600' },
  code_failed: { label: 'Code failed', cls: 'bg-rose-500/15 text-rose-600' },
  code_burned: { label: 'Code burned', cls: 'bg-gray-400/20 text-gray-500' },
};

const KIND_FILTERS: Array<{ value: FleetAuditEvent['kind'] | null; label: string }> = [
  { value: null, label: 'All' },
  { value: 'command_created', label: 'Created' },
  { value: 'command_state', label: 'State' },
  { value: 'command_refused', label: 'Refused' },
  { value: 'code_issued', label: 'Code issued' },
  { value: 'code_redeemed', label: 'Code redeemed' },
  { value: 'code_failed', label: 'Code failed' },
  { value: 'code_burned', label: 'Code burned' },
];

/** Result pill: what happened, read off `detail`/`kind` — never a fabricated status. */
function resultPill(e: FleetAuditEvent): { label: string; cls: string } {
  const detailState = (e.detail as Record<string, unknown> | null)?.state;
  if (e.kind === 'command_state' && typeof detailState === 'string') {
    if (detailState === 'done') return { label: 'Done', cls: 'bg-emerald-500/15 text-emerald-600' };
    if (detailState === 'failed' || detailState === 'expired') return { label: detailState === 'failed' ? 'Failed' : 'Expired', cls: 'bg-rose-500/15 text-rose-600' };
    if (detailState === 'cancelled') return { label: 'Cancelled', cls: 'bg-gray-400/20 text-gray-500' };
    return { label: detailState, cls: 'bg-amber-500/15 text-amber-600' };
  }
  return KIND_META[e.kind] || { label: e.kind, cls: 'bg-gray-400/20 text-gray-500' };
}

function whenLabel(at: number): string {
  const d = new Date(at);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay ? d.toLocaleTimeString() : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const LIMIT = 50;

export default function AuditView({ deviceId, registerName, onClose }: {
  deviceId: string;
  registerName: string;
  onClose: () => void;
}) {
  const [kind, setKind] = useState<FleetAuditEvent['kind'] | null>(null);
  const [actorFilter, setActorFilter] = useState('');
  const [actorInput, setActorInput] = useState('');
  const [events, setEvents] = useState<FleetAuditEvent[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (reset: boolean) => {
    if (reset) { setLoading(true); setError(null); } else { setLoadingMore(true); }
    try {
      const res = await fleetSupportApi.getAudit({
        deviceId,
        kind: kind || undefined,
        actor: actorFilter || undefined,
        limit: LIMIT,
        cursor: reset ? undefined : cursor,
      });
      setEvents((prev) => (reset ? res.events : [...prev, ...res.events]));
      setCursor(res.cursor);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Could not load the audit trail');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, kind, actorFilter, cursor]);

  useEffect(() => {
    void load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId, kind, actorFilter]);

  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center bg-black/40 p-4" onClick={onClose} role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Command history — ${registerName}`}
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border-2 bg-white shadow-2xl"
        style={{ borderColor: '#DC143C' }}
      >
        <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-3">
          <div>
            <div className="text-[14px] font-extrabold text-gray-900">Command history — {registerName}</div>
            <div className="text-[11.5px] text-gray-400">every action, who fired it, what happened</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-2.5">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.label}
              type="button"
              onClick={() => setKind(kind === f.value ? null : f.value)}
              className={`min-h-[44px] rounded-xl px-3 text-[11.5px] font-extrabold ${
                kind === f.value ? 'bg-emerald-500/20 text-emerald-700' : 'text-gray-400 hover:bg-gray-100'
              }`}
            >
              {f.label}
            </button>
          ))}
          <form
            className="ml-auto flex items-center gap-1.5"
            onSubmit={(e) => { e.preventDefault(); setActorFilter(actorInput.trim()); }}
          >
            <input
              type="text"
              value={actorInput}
              onChange={(e) => setActorInput(e.target.value)}
              placeholder="Filter by actor…"
              className="h-[44px] w-[160px] rounded-lg border border-gray-300 px-2.5 text-[12.5px] text-gray-900"
            />
            <button type="submit" className="min-h-[44px] rounded-lg border border-gray-300 px-3 text-[12px] font-semibold text-gray-600 hover:bg-gray-50">
              Filter
            </button>
          </form>
        </div>

        <div className="flex-1 overflow-y-auto">
          {error && (
            <div role="alert" className="m-4 flex items-start gap-2 rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-[11.5px] font-bold text-rose-700">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Could not load the audit trail — {error}
            </div>
          )}

          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="sticky top-0 bg-gray-50 text-left text-[10.5px] font-bold uppercase tracking-wide text-gray-400">
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">By</th>
                <th className="px-3 py-2">Reason</th>
                <th className="px-3 py-2">Result</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={5} className="px-3"><RubyLoader variant="section" label="Loading…" /></td></tr>
              )}
              {!loading && !error && events.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400">No audit events for this device yet.</td></tr>
              )}
              {events.map((e) => {
                const pill = resultPill(e);
                return (
                  <tr key={e.eventId} className="border-t border-gray-100 align-top text-gray-600">
                    <td className="whitespace-nowrap px-3 py-2 font-mono text-[11.5px] text-gray-500">{whenLabel(e.at)}</td>
                    <td className="px-3 py-2 text-gray-900">{e.action ? e.action.replace(/_/g, ' ') : KIND_META[e.kind]?.label || e.kind}</td>
                    <td className="px-3 py-2 font-semibold">{e.actor}</td>
                    <td className="px-3 py-2 text-gray-500">{e.reason || '—'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10.5px] font-extrabold ${pill.cls}`}>
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {pill.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="border-t border-gray-200 px-4 py-3">
          {cursor && !loading && (
            <button
              type="button"
              onClick={() => void load(false)}
              disabled={loadingMore}
              className="min-h-[44px] w-full rounded-lg border border-gray-300 text-[12.5px] font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {loadingMore ? 'Loading…' : 'Load more'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
